import React from 'react';
import { RegionalTable } from './RegionalTable';
import { getColumnDefsByType, PAGE_SIZE } from './utils';
import { STATES_MAP_CODES } from '../utils';
import { getFormattedSpendWithSymbol } from 'utils/number';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import ChangeTypeModal from 'Details/ChangeTypeModal/ChangeTypeModal';
import TopVendorsModal from 'Details/TopVendorsModal';
import MoveToBinModal from 'Details/MoveToBinModal/MoveToBinModal';
import CategorisationDialog from 'Explore/CategorisationDialog';
import { SPEND_TYPE } from 'Data/constants';
import { getLocationsData, changeCategorisation } from 'utils/apiRequests';
import { TAIL_SPEND } from 'utils/detailsChartDataConverter';
import { INDIRECT, DIRECT } from 'Pmi/constants';
import { PmiContext } from '../../context';

export class RegionalTableContainer extends React.Component {
	state = {
		data: [],
		totalCount: 0,
		isLoading: false,
		headers: [],
		page: 1,
		sortModel: null,
		categorisationModalData: null,
		changeStatusModalData: null,
		topVendorsModalData: null,
		moveToBinModalData: null,
	};

	static contextType = PmiContext;

	componentDidMount() {
		this.fetchData();
	}

	componentDidUpdate(prevProps) {
		const {
			queryParams,
			dimension,
			categoryType,
			selectedCountry,
			selectedState,
			showTailSpend,
		} = this.props;

		if (
			selectedCountry !== prevProps.selectedCountry ||
			selectedState !== prevProps.selectedState ||
			categoryType !== prevProps.categoryType ||
			dimension.value !== prevProps.dimension.value ||
			showTailSpend !== prevProps.showTailSpend ||
			!isEqual(queryParams, prevProps.queryParams)
		) {
			this.setState({ page: 1, sortModel: null }, this.fetchData);
		}
	}

	getSortParams = () => {
		const { headers, sortModel } = this.state;

		if (!sortModel) {
			return {};
		}

		const column = headers.find(item => item.colName === sortModel.colId);

		return {
			sortDirection: sortModel && sortModel.sort,
			sortIndex: column && column.sortIndex,
		};
	};

	getCategoryDimension = () => {
		const { categoryType, queryParams } = this.props;

		if (categoryType !== 'category') {
			return categoryType;
		}

		const level = Math.min(queryParams.hierarchy.length + 1, 4);

		return `categoryL${level}`;
	};

	getApiParams = () => {
		const {
			dimension,
			queryParams,
			region,
			selectedCountry,
			selectedState,
			showTailSpend,
			spendType,
		} = this.props;
		const { page } = this.state;
		const isPurchasing = dimension.value === 'purchasing';
		const regionFilter = `${dimension.value}Region`;
		const countryFilter = `${dimension.value}Country`;
		const stateFilter = `${dimension.value}State`;
		const showState = !!STATES_MAP_CODES[selectedCountry];

		return {
			...queryParams,
			...this.getSortParams(),
			isPurchasing,
			...(spendType !== 0 ? { includeTailSpend: !showTailSpend } : {}),
			includeState: showState,
			take: PAGE_SIZE,
			skip: PAGE_SIZE * (page - 1),
			dimension: this.getCategoryDimension(),
			filters: [
				...queryParams.filters,
				regionFilter,
				...(selectedCountry ? [countryFilter] : []),
				...(selectedState ? [stateFilter] : []),
			],
			filterValues: [
				...queryParams.filterValues,
				region,
				...(selectedCountry ? [selectedCountry] : []),
				...(selectedState ? [selectedState] : []),
			],
		};
	};

	fetchData = () => {
		const { page, data } = this.state;

		this.setState({ isLoading: true });

		return getLocationsData(this.getApiParams())
			.then(({ headers, body, totalCount }) => {
				this.setState({
					data: page === 1 ? body : [...data, ...body],
					headers,
					totalCount,
					isLoading: false,
				});
			})
			.catch(() => {
				this.setState({ isLoading: false });
			});
	};

	spendFormatter = value => {
		const { currencySymbol } = this.props;

		return value
			? getFormattedSpendWithSymbol(value, currencySymbol).toUpperCase()
			: '-';
	};

	getColumnDefs = () => {
		const { companies, categoryType, selectedCountry, spendType } = this.props;
		const showState = !!STATES_MAP_CODES[selectedCountry];
		const { sortModel } = this.state;
		const showTotalSpend = isNumber(spendType);

		return getColumnDefsByType(
			categoryType,
			companies,
			this.spendFormatter,
			showState,
			sortModel,
			showTotalSpend
		);
	};

	handleLoadMore = () => {
		const { page } = this.state;

		this.setState({ page: page + 1 }, this.fetchData);
	};

	handleSortChanged = ([sortModel]) => {
		if (sortModel) {
			this.setState({ sortModel, page: 1 }, this.fetchData);
		}
	};

	handleViewTransactionsClick = row => {
		const {
			pmiBreadcrumbs,
			showExplorePage,
			categoryType,
			dimension,
			selectedCountry,
			showTailSpend,
		} = this.props;
		const options = {};
		const exploreHierarchy = pmiBreadcrumbs.filter(item => item !== TAIL_SPEND);
		const spendType =
			exploreHierarchy && exploreHierarchy.length > 0
				? exploreHierarchy.shift()
				: null;

		if (spendType) {
			options.spendType = spendType.toUpperCase();
		}

		if (categoryType === 'category') {
			exploreHierarchy.push(row.name);
		} else if (categoryType === 'categoryL4') {
			options.categoryL4 = row.name;
		} else {
			options.vendorName = row.name;
		}

		if (showTailSpend) {
			options.isTailSpend = false;
		}

		const showState = !!STATES_MAP_CODES[selectedCountry];
		if (showState) {
			options[`${dimension.value}LocationCountry`] = selectedCountry;
			options[`${dimension.value}LocationState`] = row.location;
		} else {
			options[`${dimension.value}LocationCountry`] = row.location;
		}

		if (exploreHierarchy && exploreHierarchy.length > 0) {
			options.hierarchy = exploreHierarchy;
		}

		showExplorePage(options, row.name);
	};

	getTailSpendRow = () => {
		const { tailSpend } = this.props;

		if (!tailSpend) {
			return [];
		}

		return [
			{
				totalSpend: tailSpend.spend,
				indirectSpend: tailSpend.spend,
				name: 'Tail spend',
				location: '-',
			},
		];
	};

	getVendorTransactionsParams = () => {
		const { categorisationModalData } = this.state;

		if (!categorisationModalData) {
			return {};
		}

		return {
			filters: ['harmonizedSupplier'],
			filterValues: [categorisationModalData.name],
		};
	};

	getContextParams = () => {
		const { selectedCountry, dimension } = this.props;
		const { categorisationModalData } = this.state;

		if (!categorisationModalData) {
			return {};
		}

		const showState = !!STATES_MAP_CODES[selectedCountry];
		const countryFilter = `${dimension.value}Country`;
		const stateFilter = `${dimension.value}State`;
		const locationParam = showState ? stateFilter : countryFilter;
		const locationValue = categorisationModalData.location;
		const apiParams = this.getApiParams();
		const hasLocationParam = apiParams.filters.includes(locationParam);
		const apiParamsContext = {
			dateFrom: apiParams.dateFrom,
			dateTo: apiParams.dateTo,
			hierarchy: apiParams.hierarchy,
			filters: apiParams.filters,
			filterValues: apiParams.filterValues,
			...(!hasLocationParam
				? {
						filters: [...apiParams.filters, locationParam],
						filterValues: [...apiParams.filterValues, locationValue],
				  }
				: {}),
		};

		return {
			...apiParamsContext,
			filters: [...apiParamsContext.filters, 'harmonizedSupplier'],
			filterValues: [
				...apiParamsContext.filterValues,
				categorisationModalData.name,
			],
		};
	};

	handleChangeCategorisation = ({ taxonomy, isDirect, isAllTransactions }) => {
		const params = isAllTransactions
			? this.getVendorTransactionsParams()
			: this.getContextParams();
		const spendType = isDirect ? SPEND_TYPE.DIRECT : SPEND_TYPE.INDIRECT;
		const data = !isDirect
			? {
					l4Code: taxonomy.aP_L4Code,
			  }
			: {
					l1Category: taxonomy.project_L1Category,
					l2Category: taxonomy.project_L2Category,
					l3Category: taxonomy.project_L3Category,
					l4Category: taxonomy.project_L4Category,
			  };

		return changeCategorisation(spendType, params, data).then(() => {
			const { refreshPmiContent } = this.context;
			if (refreshPmiContent) {
				refreshPmiContent();
			}
		});
	};

	handleChangeStatus = row => {
		const {
			pmiBreadcrumbs,
			categoryType,
			companies,
			dimension,
			region,
			selectedCountry,
			selectedState,
		} = this.props;
		const exploreHierarchy = pmiBreadcrumbs.filter(item => item !== TAIL_SPEND);
		const spendType =
			exploreHierarchy && exploreHierarchy.length > 0
				? exploreHierarchy.shift()
				: null;

		// Regional variables
		const showState = !!STATES_MAP_CODES[selectedCountry];
		const countryFilter = `${dimension.value}Country`;
		const stateFilter = `${dimension.value}State`;
		const regionFilter = `${dimension.value}Region`;
		const locationParam = showState ? stateFilter : countryFilter;
		const locationValue = row.location;

		// Company, region, country, state params
		const extraFilters = {
			company: companies,
			[regionFilter]: [region],
			...(selectedCountry ? { [countryFilter]: [selectedCountry] } : {}),
			...(selectedState ? { [stateFilter]: [selectedState] } : {}),
			[locationParam]: [locationValue],
		};

		const data = { isPmi: true, extraFilters };

		if (categoryType === 'category') {
			data.categoryHierarchy = [...exploreHierarchy, row.name];
		} else if (categoryType === 'categoryL4') {
			data.hierarchy = exploreHierarchy.length > 0 ? exploreHierarchy : null;
			data.categoryName = row.name;
		} else {
			data.hierarchy = exploreHierarchy.length > 0 ? exploreHierarchy : null;
			data.selectedDimensionKey = 'harmonizedSupplier';
			data.selectedDimensionKeyTitle = 'vendor';
			data.selectedDimensionName = row.name;
		}

		if (spendType) {
			data.spendType = spendType.toUpperCase();
		}

		this.setState({
			changeStatusModalData: data,
		});
	};

	showTopVendorsModal = row => {
		const {
			pmiBreadcrumbs,
			categoryType,
			companies,
			dimension,
			region,
			selectedCountry,
			selectedState,
			spendType,
			showTailSpend,
		} = this.props;
		const hierarchy = pmiBreadcrumbs.filter(
			item => ![DIRECT, INDIRECT, TAIL_SPEND].includes(item)
		);

		// Regional variables
		const showState = !!STATES_MAP_CODES[selectedCountry];
		const countryFilter = `${dimension.value}Country`;
		const stateFilter = `${dimension.value}State`;
		const regionFilter = `${dimension.value}Region`;
		const locationParam = showState ? stateFilter : countryFilter;
		const locationValue = row.location;

		// Company, region, country, state params
		const extraFilters = {
			company: companies,
			[regionFilter]: [region],
			...(selectedCountry ? { [countryFilter]: [selectedCountry] } : {}),
			...(selectedState ? { [stateFilter]: [selectedState] } : {}),
			[locationParam]: [locationValue],
			...(spendType != null ? { spendType: [spendType] } : {}),
		};

		const data = {
			hierarchy,
			includeTailSpend: !showTailSpend,
			name: row.name,
			extraFilters,
		};

		if (categoryType === 'category') {
			data.hierarchy = [...data.hierarchy, row.name];
		} else if (categoryType === 'categoryL4') {
			data.extraFilters = { ...data.extraFilters, categoryL4: [row.name] };
		} else {
			data.extraFilters = {
				...data.extraFilters,
				harmonizedSupplier: [row.name],
			};
		}

		this.setState({
			topVendorsModalData: data,
		});
	};

	hideTopVendorsModal = () => {
		this.setState({
			topVendorsModalData: null,
		});
	};

	handleMoveToBin = row => {
		const {
			pmiBreadcrumbs,
			categoryType,
			companies,
			dimension,
			region,
			selectedCountry,
			selectedState,
		} = this.props;
		const exploreHierarchy = pmiBreadcrumbs.filter(item => item !== TAIL_SPEND);
		const spendType =
			exploreHierarchy && exploreHierarchy.length > 0
				? exploreHierarchy.shift()
				: null;

		// Regional variables
		const showState = !!STATES_MAP_CODES[selectedCountry];
		const countryFilter = `${dimension.value}Country`;
		const stateFilter = `${dimension.value}State`;
		const regionFilter = `${dimension.value}Region`;
		const locationParam = showState ? stateFilter : countryFilter;
		const locationValue = row.location;

		// Company, region, country, state params
		const extraFilters = {
			company: companies,
			[regionFilter]: [region],
			...(selectedCountry ? { [countryFilter]: [selectedCountry] } : {}),
			...(selectedState ? { [stateFilter]: [selectedState] } : {}),
			[locationParam]: [locationValue],
		};

		const data = { isPmi: true, extraFilters };

		if (categoryType === 'category') {
			data.categoryHierarchy = [...exploreHierarchy, row.name];
		} else if (categoryType === 'categoryL4') {
			data.hierarchy = exploreHierarchy.length > 0 ? exploreHierarchy : null;
			data.categoryName = row.name;
		} else {
			data.hierarchy = exploreHierarchy.length > 0 ? exploreHierarchy : null;
			data.selectedDimensionKey = 'harmonizedSupplier';
			data.selectedDimensionKeyTitle = 'vendor';
			data.selectedDimensionName = row.name;
		}

		if (spendType) {
			data.spendType = spendType.toUpperCase();
		}

		this.setState({
			moveToBinModalData: data,
		});
	};

	showCategorisationModal = categorisationModalData => {
		this.setState({ categorisationModalData });
	};

	hideCategorisationModal = () => {
		this.setState({ categorisationModalData: null });
	};

	render() {
		const {
			isLoading,
			data,
			totalCount,
			categorisationModalData,
			changeStatusModalData,
			topVendorsModalData,
			moveToBinModalData,
		} = this.state;
		const { categoryType } = this.props;
		const { refreshPmiContent } = this.context;

		const apiParams = this.getApiParams();
		return (
			<React.Fragment>
				<RegionalTable
					isLoading={isLoading}
					data={data}
					pinnedBottomRowData={this.getTailSpendRow()}
					totalCount={totalCount}
					loadMore={this.handleLoadMore}
					onSortChanged={this.handleSortChanged}
					columnDefs={this.getColumnDefs()}
					showCategorisationModal={
						categoryType === 'vendor' ? this.showCategorisationModal : null
					}
					onViewTransactionsClick={this.handleViewTransactionsClick}
					onTopVendorsClick={this.showTopVendorsModal}
					onChangeStatus={this.handleChangeStatus}
					onMoveToBin={this.handleMoveToBin}
					queryParams={apiParams}
				/>
				{topVendorsModalData && (
					<TopVendorsModal
						isPmi
						onClose={this.hideTopVendorsModal}
						{...topVendorsModalData}
					/>
				)}
				{categorisationModalData && (
					<CategorisationDialog
						name={categorisationModalData.name}
						onClose={this.hideCategorisationModal}
						onSave={this.handleChangeCategorisation}
						contextParams={this.getContextParams()}
						allTransactionsParams={this.getVendorTransactionsParams()}
					/>
				)}
				{changeStatusModalData && (
					<ChangeTypeModal
						isOpen={changeStatusModalData}
						isHarmonized
						{...changeStatusModalData}
						onClose={() => {
							this.setState({ changeStatusModalData: null });
						}}
						onSubmit={refreshPmiContent}
					/>
				)}
				{moveToBinModalData && (
					<MoveToBinModal
						isOpen={moveToBinModalData}
						isHarmonized
						{...moveToBinModalData}
						onClose={() => {
							this.setState({ moveToBinModalData: null });
						}}
						onSubmit={refreshPmiContent}
					/>
				)}
			</React.Fragment>
		);
	}
}

export default RegionalTableContainer;
